import { useMutation, useQueryClient } from '@tanstack/react-query'
import { saveTeamUpdate, generateTeamUpdate, DEFAULT_META } from '../data/metaApi.js'
import { useHistoryStore } from '../store/historyStore.js'
import { rememberTeamUpdate } from '../data/teamUpdateOverrides.js'
import { withArchiveGuard } from './_archiveGuard.js'

/**
 * useSaveTeamUpdate — save one team's weekly update via the server-side
 * partial-update endpoint.
 *
 * Input: { teamId, patch? | entry? }
 *   patch  (preferred): object with only the field(s) being changed.
 *          Server merges onto current KV state, so Ricky editing
 *          TLDR doesn't clobber Katie's Wins written a moment earlier.
 *   entry  (legacy): full entry. Kept for call-sites that genuinely
 *          need full-replace semantics.
 *
 * Flow mirrors useSaveWeeklySummary exactly.
 */
export function useSaveTeamUpdate() {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async ({ teamId, entry, patch }) => {
      const savedEntry = await saveTeamUpdate({ teamId, entry, patch })
      return { teamId, entry: savedEntry }
    },

    onMutate: async ({ teamId, entry, patch }) => {
      await queryClient.cancelQueries({ queryKey: ['meta'] })
      const previous = queryClient.getQueryData(['meta']) || DEFAULT_META

      useHistoryStore
        .getState()
        .pushSnapshot(
          'Team update',
          queryClient.getQueryData(['goals']),
          previous
        )

      const existingEntry = previous.teamUpdates?.[teamId] || {}
      const optimisticEntry = patch
        ? { ...existingEntry, ...patch }
        : entry
      queryClient.setQueryData(['meta'], {
        ...previous,
        teamUpdates: {
          ...(previous.teamUpdates || {}),
          [teamId]: optimisticEntry,
        },
      })

      return { previous }
    },

    onSuccess: ({ teamId, entry }) => {
      const current = queryClient.getQueryData(['meta']) || DEFAULT_META
      queryClient.setQueryData(['meta'], {
        ...current,
        teamUpdates: {
          ...(current.teamUpdates || {}),
          [teamId]: entry,
        },
      })
      rememberTeamUpdate(teamId, entry)
    },

    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(['meta'], ctx.previous)
    },
  })
  return withArchiveGuard(mutation)
}

/**
 * useGenerateTeamUpdate — POST to /ai-team-update with goals + sub-goals
 * owned by the team DRI; receives { tldr, wins, flags } back.
 *
 * Does NOT persist — the caller takes the response and writes each
 * field via useSaveTeamUpdate (or by populating the local draft state
 * and letting the user save normally).
 */
export function useGenerateTeamUpdate() {
  const mutation = useMutation({
    mutationFn: async (args) => generateTeamUpdate(args || {}),
  })
  return withArchiveGuard(mutation)
}
